import { GpsPoint } from '../telemetry/processed/GpsPoint'

/**
 * Represents a specific GPS point within the context of a lap.
 *
 * This is a platform-agnostic type that associates a GPS point with
 * its parent lap metadata. Used for:
 * - Cross-lap point comparison (time drift analysis)
 * - Synchronized markers across charts/maps/videos
 * - Identifying "where in which lap" for any UI component
 *
 * Unlike the deprecated LapElapsedTimeDistance, this type:
 * - Uses platform-agnostic GpsPoint (no Leaflet dependency)
 * - Contains only essential lap context
 * - Can be converted to UI-specific types at the boundary
 */
export interface LapPoint {
  /** The lap this point belongs to */
  lapId: string
  /** Lap number within the session */
  lapNumber: number
  /** The GPS point data (position, speed, distance, elapsed time) */
  gpsPoint: GpsPoint
}

/**
 * Create a LapPoint from a lap and GPS point
 */
export function createLapPoint(
  lapId: string,
  lapNumber: number,
  gpsPoint: GpsPoint
): LapPoint {
  return {
    lapId,
    lapNumber,
    gpsPoint,
  }
}

/**
 * Create a LapPoint from a Lap instance and point index
 */
export function createLapPointFromIndex(
  lap: { id: string; lapNumber: number; getGpsPoints(): GpsPoint[] },
  pointIndex: number
): LapPoint | null {
  const points = lap.getGpsPoints()
  if (pointIndex < 0 || pointIndex >= points.length) {
    return null
  }
  return createLapPoint(lap.id, lap.lapNumber, points[pointIndex])
}
